import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, RotateCw, Search } from 'lucide-react';
import { vipAPI } from '../../../services/api';
import { useBrand } from '../../../context/BrandContext';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card } from '../../ui/card';
import { Spinner } from '../../ui/spinner';
import { Alert, AlertDescription } from '../../ui/alert';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { cn } from '../../../lib/utils';
import { issuedRowView, STATUS_FILTERS } from './issuedList';
import { filtersFromSearch, issuedQuery, searchWithFilters, searchWithPatch } from './issuedFilters';
import { CreatedRange, ManagerFilter, Pager } from './IssuedFilterControls';
import { CancelCell, CommentCell } from './IssuedCells';

const PAGE_SIZE = 50;

function IssuedRow({ row, confirming, cancelling, onAsk, onCancel, onKeep, onOpenPlayer }) {
  const v = issuedRowView(row);
  return (
    <TableRow className={cn(row.status === 'cancelled' && 'opacity-60')}>
      <TableCell className="px-3 py-2 align-top">
        <div className="whitespace-nowrap text-xs font-bold text-ink">{v.added}</div>
        <div className="text-xs font-medium text-ink-mute">{v.addedBy}</div>
        {v.changed && <div className="text-xs font-medium text-ink-mute">{v.changed}</div>}
      </TableCell>
      <TableCell className="px-3 py-2 align-top">
        <button type="button" className="text-left" onClick={() => onOpenPlayer(String(v.userId))} title="Check this player">
          <div className="text-sm font-bold text-brand-blue hover:underline">{v.player}</div>
          <div className="text-xs font-medium text-ink-mute">ID {v.userId}</div>
        </button>
      </TableCell>
      <TableCell className="px-3 py-2 align-top">
        <div className="whitespace-nowrap text-sm font-bold text-ink">{v.amount}</div>
        <div className="text-xs font-medium text-ink-mute">{v.wagering} · {v.title}</div>
        {v.bonusUrl && (
          <a href={v.bonusUrl} target="_blank" rel="noopener noreferrer" title="Open the bonus in the back office"
            className="mt-0.5 flex items-center gap-1 font-mono text-xs font-medium text-brand-blue hover:underline">
            #{v.bonusId} <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </TableCell>
      <TableCell className="px-3 py-2 align-top">
        {v.eligible ? (
          <>
            <div className="whitespace-nowrap text-sm font-medium text-ink">{v.eligible}</div>
            {v.rate && <div className="whitespace-nowrap font-mono text-xs font-medium text-ink-mute">{v.rate}</div>}
          </>
        ) : <span className="text-xs font-medium text-ink-mute">—</span>}
      </TableCell>
      <TableCell className="px-3 py-2 align-top text-xs font-medium text-ink-dim">{v.period}</TableCell>
      <TableCell className="px-3 py-2 align-top">
        <div className="flex flex-wrap gap-1">
          <Badge variant={v.status.variant}>{v.status.label}</Badge>
          {v.override && <Badge variant="warning" title="Issued although a check failed">Override</Badge>}
        </div>
      </TableCell>
      <TableCell className="max-w-xs px-3 py-2 align-top">
        <CommentCell comment={v.comment} />
      </TableCell>
      <TableCell className="px-3 py-2 text-right align-top">
        <CancelCell view={v} confirming={confirming} cancelling={cancelling} onAsk={onAsk} onCancel={onCancel} onKeep={onKeep} />
      </TableCell>
    </TableRow>
  );
}

// IssuedBonuses lists the comps saved in bi_backoffice.vip_comp_bonuses_hist, newest first, with
// filters by status, added date, manager and player; pending ones can be cancelled here. The
// filters and page live in the URL (see issuedFilters), so a filtered list can be linked.
export default function IssuedBonuses({ onOpenPlayer, notify }) {
  const { activeBrand } = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const { status, page, from, to, manager, q } = filtersFromSearch(searchParams);
  // an email search is kept here only: the URL holds user IDs, not emails
  const [emailSearch, setEmailSearch] = useState('');
  const search = q || emailSearch;
  const [searchText, setSearchText] = useState(search);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  // setFilters changes some filters, going back to the first page unless the patch sets one.
  // Each change is a history entry, so Back returns to the previous page or filter; a click that
  // changes nothing (the selected status again) does not navigate.
  const setFilters = (patch) => {
    const next = searchWithPatch(searchParams, patch);
    if (next.toString() !== searchParams.toString()) setSearchParams(next);
  };
  const setPage = (p) => setFilters({ page: p });

  // The link always says which status it shows (opening the tab lands on ?status=pending).
  useEffect(() => {
    if (!searchParams.has('status')) setSearchParams((sp) => searchWithFilters(sp, filtersFromSearch(sp)), { replace: true });
  }, [searchParams, setSearchParams]);

  // The box follows the active search: a user ID from the URL (a shared link, back/forward) or
  // the email submitted here; it empties when neither is set.
  useEffect(() => { setSearchText(q || emailSearch); }, [q, emailSearch]);

  // Only the latest request may update the list (filters and brand can change mid-flight).
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await vipAPI.getIssuedBonuses(issuedQuery({ status, page, from, to, manager, q: search }, PAGE_SIZE));
      if (requestId !== requestIdRef.current) return;
      const { items, total } = res.data;
      // the page emptied (cancellations, a narrower filter): step back to the last page with rows
      if (items.length === 0 && total > 0 && page > 1) {
        setSearchParams((sp) => searchWithPatch(sp, { page: Math.ceil(total / PAGE_SIZE) }), { replace: true });
        return;
      }
      setData(res.data);
    } catch (e) {
      if (requestId === requestIdRef.current) setError(e?.response?.data?.error || 'Loading the issued bonuses failed.');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [status, page, from, to, manager, search, setSearchParams]);

  useEffect(() => { load(); }, [load, activeBrand]);

  // applySearch filters by a user ID (digits) or an email; empty clears the filter.
  const applySearch = (e) => {
    e.preventDefault();
    const text = searchText.trim();
    if (text && !/^\d+$/.test(text) && !text.includes('@')) {
      setError('Enter a numeric user ID or an email address.');
      return;
    }
    const isEmail = text.includes('@');
    setEmailSearch(isEmail ? text : '');
    setFilters({ q: isEmail ? '' : text });
  };

  const cancel = async (id) => {
    setCancellingId(id);
    try {
      const res = await vipAPI.cancelBonus(id);
      setData((d) => ({ ...d, items: d.items.map((r) => (r.id === id ? { ...r, ...res.data } : r)) }));
      notify('Bonus cancelled.', 'success');
      setConfirmId(null);
    } catch (e) {
      notify(e?.response?.data?.error || 'Cancelling the bonus failed.', 'error');
    } finally {
      setCancellingId(null);
    }
  };

  const total = data?.total || 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return (
    <Card className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key || 'all'} type="button" aria-pressed={status === f.key}
            onClick={() => setFilters({ status: f.key })}
            className={cn('rounded-full px-3 py-1.5 text-xs font-bold',
              status === f.key ? 'bg-ink text-surface' : 'bg-surface-2 text-ink-dim hover:text-ink')}
          >
            {f.label}
          </button>
        ))}
        <span className="flex-1" />
        <CreatedRange from={from} to={to} onChange={setFilters} />
        <ManagerFilter value={manager} managers={data?.managers} onChange={(m) => setFilters({ manager: m })} />
        <form onSubmit={applySearch} className="flex items-center gap-2">
          <Input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="User ID or email" aria-label="Filter by user ID or email" className="h-8 w-56 text-xs" />
          <Button type="submit" variant="outline" size="sm" className="gap-1.5"><Search className="h-3.5 w-3.5" /> Filter</Button>
        </form>
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={load} disabled={loading}><RotateCw className="h-3.5 w-3.5" /> Refresh</Button>
      </div>

      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
      {loading && !data && <div className="flex justify-center py-10"><Spinner /></div>}
      {data && data.items.length === 0 && <p className="py-6 text-center text-xs font-medium text-ink-mute">No bonuses match.</p>}
      {data && data.items.length > 0 && (
        <div className={cn('overflow-x-auto', loading && 'opacity-60')}>
          <Table>
            <TableHeader>
              <TableRow>
                {['Added', 'Player', 'Bonus', 'Eligible', 'Period', 'Status', 'Comment', ''].map((h) => (
                  <TableHead key={h || 'actions'} className="h-9 px-3 text-xs">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((row) => (
                <IssuedRow
                  key={row.id} row={row} onOpenPlayer={onOpenPlayer}
                  confirming={confirmId === row.id} cancelling={cancellingId === row.id}
                  onAsk={() => setConfirmId(row.id)} onKeep={() => setConfirmId(null)} onCancel={() => cancel(row.id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {data && total > 0 && (
        <Pager data={data} page={page} lastPage={lastPage} disabled={loading} onPage={setPage} />
      )}
    </Card>
  );
}
